import * as Sentry from "@sentry/react";

/**
 * NO URL-FRAGMENT SCRUBBER HERE, AND THAT IS A STANDING CONDITION RATHER THAN AN OVERSIGHT.
 *
 * fleet-driver's `sentry.ts` carries a 42-line `beforeSend` / `beforeSendTransaction` pass
 * that truncates every URL-bearing string at the first `#`. It exists because that app has a
 * PUBLIC signing route: a witness or a deposit payer holds no account, and their 256-bit
 * signing token rides in `location.hash` — which browsers never put on the wire, but an
 * error reporter is not a browser. It reads `location.href` itself and would ship that token
 * to Sentry as an event URL, a transaction name or a navigation breadcrumb, turning a
 * credential the network never saw into telemetry.
 *
 * THIS APP HAS NO SUCH CREDENTIAL. Every partner who signs a Vehicle Schedule signs in their
 * own session: `agreement_sign`'s PARTNER arm binds `my_partner_id()` to the vehicle's
 * current partner and to the partner the referent records, and admits no token at all
 * (fleet-backend `20260908020000_no_driver_signs_before_the_vehicles_partner_has.sql:1857`).
 * There is no `/sign/link`, no `/sign/witness`, and no token transport of any kind [R2] — the
 * only thing in a URL here is a document id, which is not a secret and is useless without the
 * session that may read it.
 *
 * ── THE CONDITION, FOR WHOEVER ADDS THE ROUTE LATER ──────────────────────────
 * A link-signing route, a magic link, a password-reset fragment, an OAuth `access_token` in a
 * hash — ANY of them makes this file wrong the moment it lands, silently and retroactively:
 * Sentry has been collecting URLs all along and would simply start collecting the secret too.
 * So the rule is: whoever introduces the first credential-in-a-fragment in this app PORTS THE
 * DRIVER'S SCRUBBER IN THE SAME CHANGE. Both hooks, not one — errors and performance events
 * are separate pipelines, and a scrub on one only is a scrub that leaks through the other.
 */

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  sampleRate: 1.0,
  tracesSampleRate: 0.1,
  environment: import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE,
});

export { Sentry };
